<template>
  <p><a href="/company/trust">Trust Center</a></p>

  <h1>Privacy policy</h1>

  <p><strong>Last updated: June 26, 2026</strong></p>

  <p>
    Thank you for visiting the Privacy Policy of SharkFoto Inc. SharkFoto is an
    AI media platform that lets you generate and edit images, video, and audio,
    apply AI Effects, use AI Models, and run a range of free file tools. This
    Privacy Policy explains how SharkFoto Inc. (collectively, “SharkFoto Inc.”,
    “SharkFoto”, “we”, “us”, or “our”) collects, uses, and shares information
    about you (“you”, “yours” or “user”) when you access or use our websites and
    services (“Services”).
  </p>
  <p>
    This Privacy Policy is provided for general information only and does not
    constitute legal advice. If you need advice about your specific situation,
    please consult a qualified professional.
  </p>
  <p>
    Users are responsible for any third-party data they provide or share through
    the Services and confirm that they have the third party's consent to provide
    such data to us.
  </p>

  <h3 id="information-we-collect">Information We Collect</h3>
  <p>
    We may collect and combine information about you when you access or use the
    Services, including:
  </p>
  <p><strong>Contact Information</strong>: such as:</p>
  <ul>
    <li>first and last name</li>
    <li>email address</li>
    <li>user profile photo</li>
  </ul>
  <p>
    <strong>Log and Usage Information: </strong>such as browser type you use,
    hardware model, operating system, IP address, unique device identifiers,
    access times, pages viewed, links clicked, and browsing behavior such as
    time spent, what site you came from, what site you visit when you leave us,
    and browsing behavior.
  </p>
  <p>
    <strong>Account Information: </strong>if you create an account on the
    Services, we collect the information you provide to us or that is shared with
    us by your chosen sign-in provider. You can sign up or sign in using your
    email address, a Google account, or an Apple account. When you use Google or
    Apple sign-in, we receive basic profile information such as your name, email
    address, and a unique account identifier from that provider, in accordance
    with their respective privacy policies.
  </p>
  <p>
    <strong>Content You Provide (Input and Output): </strong>the images, video,
    audio, prompts, files, and other materials you upload to or create with the
    Services (“Input”), and the results we generate for you (“Output”). See the
    section entitled “AI Generation &amp; Your Content” below for details on how
    we handle this content.
  </p>
  <p>
    <strong>Transactional Information: </strong>such as the plan or subscription
    you select, credits purchased or consumed, products purchased, product
    details, purchase price, purchase date, and limited payment information. We
    use Stripe to process payments; see “Accounts, Payments &amp; Subscriptions”
    below.
  </p>

  <h3 id="sources-of-information-and-tracking-technologies">
    Sources of Information, Cookies &amp; Tracking Technologies
  </h3>
  <p>
    We collect information that you directly provide to us, such as when you
    create an account, upload content, submit a form, or send us a message with
    your information.
  </p>
  <p>
    We may also receive information about you from our service providers and
    partners, including authentication providers (Google, Apple), our payment
    processor (Stripe), our hosting and analytics providers, and advertising
    partners. We may combine this information with other information we collect
    from or about you. In these cases, our Privacy Policy governs the handling of
    the combined information.
  </p>
  <p>
    We and our partners use cookies and similar technologies (such as local
    storage, web beacons, and pixels) to operate and secure the Services,
    remember your preferences, understand usage, and support advertising. Cookies
    are small text files stored on your device. The table below lists the main
    cookies and similar identifiers we use and how they are categorized.
  </p>

  <h4 id="cookies-essential">Essential</h4>
  <p>
    Required for the Services to function, including keeping you signed in and
    remembering your cookie choice. These are set by SharkFoto (first-party) and
    cannot be disabled without breaking core functionality.
  </p>
  <ul>
    <li>
      <code>sharkfoto_token</code>, <code>userId</code>,
      <code>userInfo</code> — authentication and session: identify your account
      and keep you signed in.
    </li>
    <li>
      <code>cookieStatus</code> — records whether you have acknowledged or set
      your cookie preferences.
    </li>
  </ul>

  <h4 id="cookies-functional">Functional</h4>
  <p>
    Support optional features and help us attribute referrals and tailor the
    experience. Set by SharkFoto (first-party).
  </p>
  <ul>
    <li>
      <code>reffer_id</code> — records the referral source that brought you to
      the Services (for example, a referral or partner link).
    </li>
    <li>
      <code>userIp</code> — stores your IP address to support region-aware
      functionality and fraud prevention.
    </li>
  </ul>

  <h4 id="cookies-analytics">Analytics</h4>
  <p>
    Help us understand how the Services are used so we can improve them. Provided
    by a third party (Google).
  </p>
  <ul>
    <li>
      Google Analytics 4 cookies (such as <code>_ga</code> and
      <code>_ga_*</code>) — measurement ID <code>G-33TD3X12GT</code>. Used to
      analyze usage across pages and devices.
    </li>
  </ul>

  <h4 id="cookies-advertising">Advertising</h4>
  <p>
    Support the display and measurement of ads. Provided by a third party
    (Google).
  </p>
  <ul>
    <li>
      Google AdSense cookies (publisher ID
      <code>ca-pub-6439841372484008</code>) — set when ads are served, primarily
      on our free-tools pages and in our production environment, to deliver and
      measure advertising.
    </li>
  </ul>

  <p>
    <strong>Managing cookies. </strong>You can set your browser to reject or
    delete cookies altogether or to reject our cookies in particular. However,
    this may cause some or all of our Services not to function properly. You can
    manage Google’s use of advertising cookies through your
    <a href="https://adssettings.google.com/">Google Ads Settings</a>, and opt
    out of Google Analytics across sites using the
    <a href="https://tools.google.com/dlpage/gaoptout?hl=en">Google Analytics Opt-out Browser Add-on</a>.
  </p>
  <p>
    <strong>EU/EEA users. </strong>If you are located in the EU or EEA, you can
    manage your cookie preferences through the cookie banner presented on our
    Services. We are continuing to improve our consent controls, and you may also
    use the browser and Google opt-out options described above at any time.
  </p>
  <p>
    Some websites have “do not track” features that allow you to tell a website
    not to track you. These features are not all uniform. We do not currently
    respond to those signals.
  </p>

  <h3 id="ai-generation-and-your-content">AI Generation &amp; Your Content</h3>
  <p>
    When you use our generation, editing, AI Effects, or AI Models features, the
    materials you upload or provide (“Input”) are processed to produce the
    results you request (“Output”). To generate Output, we may transmit your
    Input to third-party AI model providers (such as OpenAI, Google, and similar
    providers) that process it on our behalf to return a result. We share only
    what is needed to perform the requested generation.
  </p>
  <p>
    <strong>No training on your content. </strong>We do not use your Input or
    Output to train or fine-tune AI models, and we instruct our AI model
    providers to process your content solely to generate your requested Output
    and not to use it to train or improve their models, to the extent we are able
    to do so under our agreements with them.
  </p>
  <p>
    <strong>Ownership and commercial use. </strong>As between you and SharkFoto,
    you own the Output you generate, and you may use it for commercial purposes,
    subject to our Terms of Use and applicable law. You are responsible for
    ensuring you have the rights to any Input you provide.
  </p>
  <p>
    <strong>Retention of Input and Output. </strong>We retain your Input and
    Output associated with your account until you delete it. You can remove your
    content manually in your account settings, or contact us at
    <a href="mailto:support@sharkfoto.com">support@sharkfoto.com</a> for
    assistance. When generation fails or an error occurs, any transient data
    created during the attempt is discarded or cleaned up as part of normal
    operation, and we may retain limited diagnostic information to investigate
    errors and prevent abuse.
  </p>

  <h3 id="how-we-process-free-tools-files">How We Process Free-Tools Files</h3>
  <p>
    Our free tools handle files in two different ways depending on the format:
  </p>
  <ul>
    <li>
      <strong>Common image formats</strong> (such as JPG, PNG, and WebP) are
      processed locally in your browser whenever possible. In these cases your
      file is not uploaded to our servers.
    </li>
    <li>
      <strong>Advanced or specialized formats</strong> (such as RAW camera
      files, CAD drawings, embroidery files, and Office documents) require
      server-side conversion. These files are uploaded to our servers only to
      perform the requested conversion and are deleted shortly after processing.
      We do not retain these uploads or the converted results beyond what is
      needed to deliver them back to you.
    </li>
  </ul>

  <h3 id="accounts-payments-subscriptions">
    Accounts, Payments &amp; Subscriptions
  </h3>
  <p>
    <strong>Accounts. </strong>You can create an account using your email
    address, a Google account, or an Apple account. Depending on the method, we
    collect or receive your name, email address, profile photo (if provided), and
    a unique account identifier from the sign-in provider.
  </p>
  <p>
    <strong>Payments. </strong>We use Stripe, Inc. to process payments for paid
    plans and credits. When you make a purchase, your payment details are
    submitted directly to Stripe, which acts as our payment processor. We do not
    store your full card number; we receive limited information such as a
    transaction identifier, the result of the payment, and partial card details
    (for example, the card brand and last four digits) needed to manage your
    purchase. Stripe’s handling of your information is governed by its own
    privacy policy.
  </p>
  <p>
    <strong>Plans and credits. </strong>We process information about your plan or
    subscription, your credit balance, and your usage in order to provide paid
    features, apply entitlements, manage renewals and cancellations, and prevent
    abuse.
  </p>

  <h3 id="how-we-use-your-information">How We Use Your Information</h3>
  <p>
    We use the information we collect about you to provide, maintain, and improve
    our Services and other interactions we have with you. For example, we use the
    information collected to:
  </p>
  <ul>
    <li>Provide, operate, and improve the Services, including AI generation and file tools;</li>
    <li>
      Authenticate you, process transactions and subscriptions, and send you
      related information, including confirmations, receipts, invoices, and
      product- or Services-related notices;
    </li>
    <li>Process and deliver promotions;</li>
    <li>Respond to your comments and questions and provide customer service;</li>
    <li>
      Send you notifications or promotional messages where you have indicated
      that you wish to receive them;
    </li>
    <li>
      Detect, investigate, and prevent fraudulent transactions and other illegal
      or abusive activities, and protect our rights and property and those of
      others;
    </li>
    <li>Comply with our legal, regulatory, and financial obligations;</li>
    <li>Monitor and analyze trends, usage, and activities;</li>
    <li>
      Provide and allow our partners to provide advertising and marketing,
      including on our free-tools pages.
    </li>
  </ul>

  <h3 id="how-we-may-share-information">How We May Share Information</h3>
  <p>We may share your Personal Information in the following situations:</p>
  <ul>
    <li>
      <strong>Service Providers. </strong>We may share data with service
      providers, vendors, and contractors who perform services on our behalf,
      such as hosting (Cloudflare and AWS), payment processing (Stripe),
      authentication (Google, Apple), AI model providers, analytics, and
      advertising;
    </li>
    <li>
      <strong>Change in Business. </strong>We may share data in connection with a
      corporate transaction, such as a merger or acquisition of all or a portion
      of our business, a joint venture, corporate reorganization, insolvency or
      bankruptcy, financing, or sale of company assets;
    </li>
    <li>
      <strong>To Comply with Law. </strong>We may share data to facilitate the
      legal process and respond to lawful requests by public authorities,
      including to meet national security or law enforcement demands as permitted
      by law;
    </li>
    <li>
      <strong>With Your Consent. </strong>We may share data with third parties
      when we have your consent;
    </li>
    <li>
      <strong>With Advertising and Analytics Partners. </strong>See the section
      entitled “Advertising and Analytics” below.
    </li>
  </ul>

  <h3 id="a-dvertising-and-analytics">Advertising and Analytics</h3>
  <p>
    We use advertising and analytics technologies to understand how the Services
    are used and to support the free, ad-supported parts of our platform.
  </p>
  <p>
    <strong>Google AdSense. </strong>We use Google AdSense (publisher ID
    <code>ca-pub-6439841372484008</code>) to display advertising, primarily on
    our free-tools pages and in our production environment. Google and its
    partners may use cookies and similar technologies to serve and measure ads
    based on your visits to our Services and other sites. You can control how
    Google personalizes ads to you through your
    <a href="https://adssettings.google.com/">Google Ads Settings</a>.
  </p>
  <p>
    <strong>Google Analytics 4. </strong>We use Google Analytics 4 (measurement
    ID <code>G-33TD3X12GT</code>), an analytics service provided by Google LLC,
    to help analyze how users interact with the Services across pages and
    devices so we can improve them. To learn more, see Google’s
    <a href="https://support.google.com/analytics/answer/6004245#zippy=%2Cour-privacy-policy">Privacy Policy</a>.
    You can opt out of Google Analytics across sites by installing the
    <a href="https://tools.google.com/dlpage/gaoptout?hl=en">Google Analytics Opt-out Browser Add-on</a>.
  </p>
  <p>
    Some third-party companies may also use non-cookie technologies, such as
    statistical IDs. Your web browser may not permit you to block these
    non-cookie technologies, and browser settings that block cookies may have no
    effect on such techniques.
  </p>

  <h3 id="data-security">Data Security</h3>
  <p>
    We implement commercially reasonable security measures designed to protect
    your information. Despite our best efforts, however, no security measures are
    completely impenetrable.
  </p>

  <h3 id="data-retention">Data Retention</h3>
  <p>
    We keep your account, your uploaded content (Input), and your generated
    content (Output) until you delete them. You remain in control: you can delete
    your content and your account manually through your account settings, or
    contact us at
    <a href="mailto:support@sharkfoto.com">support@sharkfoto.com</a> for help.
  </p>
  <p>
    Files processed by our free tools are handled as described in “How We Process
    Free-Tools Files” above — common image formats are processed locally in your
    browser, and advanced-format uploads are deleted shortly after conversion and
    not retained.
  </p>
  <p>
    Even after you delete content or your account, we may retain a minimal amount
    of information where necessary to comply with our legal, tax, accounting, or
    regulatory obligations, to resolve disputes, to prevent fraud and abuse, and
    to enforce our agreements. We retain such information only for as long as
    reasonably necessary for those purposes.
  </p>

  <h3 id="international-data-transfers">International Data Transfers &amp; Storage</h3>
  <p>
    SharkFoto operates globally. The information we collect may be stored and
    processed in the United States and other countries, including by our hosting
    and storage providers (Cloudflare and AWS). If you are located outside the
    United States, you understand that we may transfer your information to, and
    process it in, the United States and other countries whose data protection
    laws may differ from those in your country.
  </p>
  <p>
    Where we transfer personal data of users in the EU/EEA or the United Kingdom
    outside of those regions, we rely on appropriate safeguards such as the
    European Commission’s Standard Contractual Clauses (and the UK equivalent) to
    protect your information.
  </p>

  <h3 id="your-rights">Your Rights</h3>
  <p>
    Wherever you are located, you can access and update much of your information
    directly in your account, and you can delete your content and your account at
    any time through your account settings. You can also contact us at
    <a href="mailto:support@sharkfoto.com">support@sharkfoto.com</a> if you need
    help exercising your choices, and you can opt out of marketing emails as
    described under “Newsletters” below.
  </p>
  <p>
    <strong>EU/EEA and UK users (GDPR). </strong>If you are located in the
    European Economic Area (EEA) or the United Kingdom, you have certain
    statutory rights under the General Data Protection Regulation (GDPR) and
    equivalent UK law in relation to your personal data, including the right to:
  </p>
  <ul>
    <li>access a copy of the personal data we hold about you;</li>
    <li>request correction of inaccurate or incomplete personal data;</li>
    <li>request deletion of your account or personal data;</li>
    <li>
      request that we restrict, or object to, the processing of your personal
      data for certain purposes;
    </li>
    <li>
      request a portable copy of certain personal data you provided to us;
    </li>
    <li>
      withdraw consent at any time where we rely on your consent (without
      affecting processing already carried out); and
    </li>
    <li>
      lodge a complaint with your local data protection supervisory authority.
    </li>
  </ul>
  <p>
    To exercise these rights, email us at
    <a href="mailto:support@sharkfoto.com">support@sharkfoto.com</a>. We will
    generally process requests within one month. We may need to request specific
    information from you to help us confirm your identity. If your request is
    complex or you have made a large number of requests, it may take us longer,
    and we will let you know if we need additional time.
  </p>
  <p><strong>Legal Bases For Processing Personal Data</strong></p>
  <p>
    Where GDPR or equivalent law applies, we process your personal data on the
    following legal grounds:
  </p>
  <ul>
    <li>
      <strong>Contractual Necessity: </strong>to enter into or perform a contract
      with you, such as providing the Services and processing your payments.
    </li>
    <li>
      <strong>Consent: </strong>where you have provided consent, for example for
      certain cookies or marketing. You may withdraw your consent at any time.
    </li>
    <li>
      <strong>Legitimate Interests: </strong>to operate, secure, and improve our
      Services, communicate with you, provide customer service, prevent fraud and
      abuse, and analyze and improve our business.
    </li>
    <li>
      <strong>Legal Obligation: </strong>to comply with our legal, tax, and
      regulatory obligations.
    </li>
  </ul>

  <h3 id="age-limitations">Age Limitations</h3>
  <p>
    Our Service is intended for adults ages 18 years and above. We do not
    knowingly collect personally identifiable information from children. If you
    are a parent or legal guardian and think your child under 13 has given us
    information, please contact us at
    <a href="mailto:support@sharkfoto.com">support@sharkfoto.com</a>. Please mark
    your inquiries as “COPPA Information Request.”
  </p>

  <h3 id="changes-to-this-privacy-policy">Changes to this Privacy Policy</h3>
  <p>
    SharkFoto Inc. may change this Privacy Policy from time to time. We encourage
    you to visit this page to stay informed. If the changes are material, we may
    provide you additional notice to your email address or through our Services.
    Your continued use of the Services indicates your acceptance of the modified
    Privacy Policy.
  </p>

  <h3 id="newsletters">Newsletters</h3>
  <p>
    You can opt out of receiving our marketing emails and/or newsletters by
    contacting us as described under “Contact Us” below, or by using the
    unsubscribe link in those emails. We may still send you transactional
    messages, which include Services-related communications and responses to your
    questions.
  </p>

  <h3 id="governing-law">Governing Law</h3>
  <p>
    This Privacy Policy and any matter relating to it are governed by the laws of
    the Hong Kong Special Administrative Region of the People’s Republic of Hong
    Kong, without regard to its conflict-of-laws principles. As described above,
    your information may be stored and processed in the United States and other
    countries; the place of storage does not change the governing law of this
    Policy.
  </p>

  <h3 id="contact-us">Contact Us</h3>
  <p>
    If you have questions, comments, or concerns about this Privacy Policy, or if
    you would like to exercise your privacy rights, you may contact us at
    <a href="mailto:support@sharkfoto.com">support@sharkfoto.com</a>.
  </p>

  <p><strong>Last updated: June 26, 2026</strong></p>
</template>

<script setup>
definePageMeta({ layout: "company" });

useHead({
  title: "Privacy policy | SharkFoto",
  meta: [
    {
      name: "description",
      content:
        "SharkFoto Inc.'s Privacy Policy: how our AI media platform collects, uses, shares, and protects your information, including AI generation and your content, free-tools file handling, accounts and Stripe payments, cookies and tracking, Google AdSense and Analytics, data retention, international transfers, and your GDPR rights.",
    },
  ],
  link: [
    {
      rel: "canonical",
      href: "https://sharkfoto.com/company/trust/privacy/",
    },
  ],
});
</script>
